'use client';

/**
 * @packageDocumentation
 * @category Component
 */

import { Combobox } from 'BomdisoftComponents/inputs/combobox/index';
import { PlaceOption } from 'BomdisoftComponents/inputs/location/place-option';
import { useLocationStore } from 'BomdisoftComponents/inputs/location/store';
import { usePlacePredictions } from 'BomdisoftComponents/inputs/location/use-place-predictions';
import { PlaceType } from 'BomdisoftComponents/types';
import { useShallow } from '@bomdisoft/store/shallow';
import { getLanguageFromLocale, noLocationsFoundT } from '@bomdisoft/translations';
import { useParams } from 'next/navigation';

type LocationsAddressInputProps = {
  label?: string;
  helperText?: string;
  required?: boolean;
  className?: string;
};

export const LocationsAddressInput = ({ label, helperText, required, className }: LocationsAddressInputProps) => {
  const params = useParams();
  const language = getLanguageFromLocale(params?.lang as string) ?? 'english';
  const [placeType, address, disableInputs, setLocationPlaceType, setLocationAddress, setDisableInputs] = useLocationStore(useShallow(s => [s.placeType, s.address, s.disableInputs, s.setLocationPlaceType, s.setLocationAddress, s.setDisableInputs]));
  const options = usePlacePredictions(address, placeType?.place_id);

  const handleInputChange = (value: string) => {
    if (disableInputs && value !== address) setDisableInputs(false);
    setLocationAddress(value);
  };
  const handleSelect = (place: PlaceType | null) => setLocationPlaceType(place);

  return (
    <Combobox
      className={className}
      clearable
      getOptionKey={place => place.place_id}
      getOptionLabel={place => place.description}
      helperText={helperText}
      inputValue={address ?? ''}
      label={label}
      noOptionsText={noLocationsFoundT[language]}
      onInputChange={handleInputChange}
      onSelect={handleSelect}
      options={options}
      renderOption={place => <PlaceOption place={place} />}
      required={required}
      testId='locations-address-input'
    />
  );
};
